import { RapidStateCreator } from "../types";
import { merge } from "lodash";
import { MediaItemUuid, MediaItem } from "@/types/media";
import { FirebaseUploadResult } from "@utils/api/firebaseStorage";

export interface CurrentMedia {
    file: MediaItem;
    frame?: MediaItem;
    frameSegment?: number;
}

export interface MediaSliceState {
    // STATE
    media: Record<MediaItemUuid, MediaItem>;
    mediaUploadPromises: Record<MediaItemUuid, Promise<FirebaseUploadResult[]>>; // TODO: remove this
    currentMedia?: CurrentMedia;
    // METHODS
    // update and delete will also update the current media if it matches the updated or deleted media item
    addMediaItem: (mediaItem: MediaItem) => void;
    updateMediaItem: (id: MediaItemUuid, attrs: Partial<MediaItem>) => void;
    deleteMediaItem: (id: MediaItemUuid) => void;
    renameMediaItem: (oldId: MediaItemUuid, newId: string, newStatus?: MediaItem["status"]) => void;
    // use this method to set the current media, NOT for media state attrs updates
    setCurrentMedia: (currentMedia?: CurrentMedia, opts?: { skipIfSet: boolean }) => void;
    deleteMediaUploadPromise: (id: MediaItemUuid) => void;
    clearMediaState: () => void;
}

export const createMediaSlice: RapidStateCreator<MediaSliceState> = (set, get) => ({
    media: {},
    mediaUploadPromises: {},
    currentMedia: undefined,
    addMediaItem: (mediaItem: MediaItem) => {
        set((state) => {
            const updatedMedia = { ...state.media };
            updatedMedia[mediaItem.id] = mediaItem;
            return { media: updatedMedia };
        });

        return mediaItem;
    },
    updateMediaItem: (id: MediaItemUuid, attrs: Partial<MediaItem>) => {
        set((state) => {
            const sourceMediaItem = state.media[id] || {};
            const updatedMediaItem = merge({}, sourceMediaItem, attrs) as MediaItem;
            const updatedMedia = { ...state.media, [id]: updatedMediaItem };
            const updatedStoreState: {
                media: Record<MediaItemUuid, MediaItem>;
                currentMedia?: CurrentMedia;
            } = { media: updatedMedia };

            // update current media (if matches the updated media item) to keep in sync
            const currentMedia = get().currentMedia;

            if (currentMedia && (currentMedia.file.id === id || currentMedia.frame?.id === id)) {
                const updatedCurrentMedia: CurrentMedia = { ...currentMedia };

                if (updatedCurrentMedia.file.id === id) {
                    updatedCurrentMedia.file = updatedMediaItem;
                }
                if (updatedCurrentMedia.frame?.id === id) {
                    updatedCurrentMedia.frame = updatedMediaItem;
                    // don't need to update frameSegment here because it is expected
                    // to be the same since the frame is the same
                }
                updatedStoreState.currentMedia = updatedCurrentMedia;
            }

            return updatedStoreState;
        });
    },
    deleteMediaItem: (id: MediaItemUuid) => {
        set((state) => {
            // Possibly disable this altogether for current media. this is already prevented
            // in the UI
            const updatedMedia = { ...state.media };
            delete updatedMedia[id];

            const updatedStoreState: {
                media: Record<MediaItemUuid, MediaItem>;
                currentMedia?: CurrentMedia;
            } = { media: updatedMedia };

            // update current media (if matches the deleted media item) to keep in sync
            const currentMedia = get().currentMedia;
            if (currentMedia && (currentMedia.file.id === id || currentMedia.frame?.id === id)) {
                let updatedCurrentMedia: CurrentMedia | undefined = { ...currentMedia };

                if (updatedCurrentMedia.file.id === id) {
                    // reset if deleting the file
                    updatedCurrentMedia = undefined;
                } else if (updatedCurrentMedia.frame?.id === id) {
                    // don't completely reset if only deleting the frame
                    updatedCurrentMedia.frame = undefined;
                    updatedCurrentMedia.frameSegment = undefined;
                }

                updatedStoreState.currentMedia = updatedCurrentMedia;
            }

            return updatedStoreState;
        });
    },
    renameMediaItem: (oldId, newId, newStatus) => {
        set((state) => {
            const item = state.media[oldId];
            if (!item) return state;
            const renamedItem: MediaItem = {
                ...item,
                id: newId,
                ...(newStatus !== undefined && { status: newStatus })
            };
            const updatedMedia = { ...state.media };
            delete updatedMedia[oldId];
            if (!state.media[newId]) {
                updatedMedia[newId] = renamedItem;
            }
            const updatedStoreState: {
                media: Record<MediaItemUuid, MediaItem>;
                currentMedia?: CurrentMedia;
            } = { media: updatedMedia };
            const currentMedia = get().currentMedia;
            if (currentMedia?.file?.id === oldId) {
                updatedStoreState.currentMedia = {
                    file: renamedItem,
                    frame: renamedItem,
                    frameSegment: currentMedia.frameSegment ?? 0
                };
            }
            return updatedStoreState;
        });
    },
    setCurrentMedia: (currentMedia?: CurrentMedia, opts?: { skipIfSet: boolean }) => {
        if (opts?.skipIfSet && get().currentMedia) {
            return;
        }

        set({ currentMedia });
    },
    deleteMediaUploadPromise: (id: MediaItemUuid) => {
        set((state) => {
            const newMediaUploadPromises = { ...state.mediaUploadPromises };
            delete newMediaUploadPromises[id];
            return { mediaUploadPromises: newMediaUploadPromises };
        });
    },
    clearMediaState: () => {
        set({ media: {}, mediaUploadPromises: {}, currentMedia: undefined });
    }
});
